import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  A TANULÓ HÁTRÁNYOS HELYZETE ÉS GYERMEKVÉDELMI ADATAI – a tanulói adatlap „SNI / BTMN” fülén
 *  (backend/lemorzsolodas.js › /helyzet). HH, HHH, RGYK, tartós gyógykezelés, Gyvt. szerinti
 *  veszélyeztetettség, ideiglenes hatályú elhelyezés / nevelésbe vétel, menekült-jogállás –
 *  időszakosan, határozattal. Ebből számol a lemorzsolódási jelzés (229/2012. Korm. r. 44/J. § (2)
 *  h), i), j), kb), kc)) és a KIR-STAT segédlet. Csak admin írja és olvassa (érzékeny adat).
 *
 *  A szülő-űrlap (formGroup) belsejében áll, ezért minden ngModel `standalone`.
 */
interface Helyzet { id?: number; tipus: string; kezdete?: string | null; vege?: string | null; hatarozat?: string | null; megjegyzes?: string | null; rogzitette?: string | null; }

@Component({
  selector: 'app-tanulo-helyzet',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  template: `
    <h3 class="th-cim">Hátrányos helyzet, gyermekvédelem</h3>
    <p class="th-halvany">A gyámhatósági határozat szerint, időszakosan. Ebből számol a lemorzsolódási jelzés (229/2012. Korm. r. 44/J. § (2))
      és a KIR-STAT (OSA4TANU a04t18: 18–20., 33–35. sor).</p>
    <div *ngIf="fut" class="th-halvany"><ion-spinner name="dots"></ion-spinner> Betöltés…</div>
    <p *ngIf="hiba" class="th-hiba">{{ hiba }}</p>
    <table class="th-tabla" *ngIf="lista.length">
      <thead><tr><th>Helyzet</th><th>Időszak</th><th>Határozat</th><th>Megjegyzés</th><th></th></tr></thead>
      <tbody>
        <tr *ngFor="let h of lista">
          <td><b>{{ tipusNev(h.tipus) }}</b><div><small>{{ tipusJogalap(h.tipus) }}</small></div></td>
          <td>{{ h.kezdete || '…' }} – {{ h.vege || '…' }}</td>
          <td>{{ h.hatarozat || '–' }}</td>
          <td>{{ h.megjegyzes || '' }}</td>
          <td>
            <ion-button size="small" fill="clear" (click)="szerkeszt(h)"><ion-icon slot="icon-only" name="create-outline"></ion-icon></ion-button>
            <ion-button size="small" fill="clear" color="danger" (click)="torol(h)"><ion-icon slot="icon-only" name="trash-outline"></ion-icon></ion-button>
          </td>
        </tr>
      </tbody>
    </table>
    <div *ngIf="!fut && !lista.length && !urlap && !hiba" class="th-halvany">Nincs rögzített hátrányos helyzet vagy gyermekvédelmi adat.</div>
    <ion-button *ngIf="!urlap && !hiba" size="small" fill="outline" (click)="szerkeszt(null)">
      <ion-icon slot="start" name="add-outline"></ion-icon>Új bejegyzés
    </ion-button>
    <div *ngIf="urlap as u" class="th-urlap">
      <ion-select label="Helyzet" label-placement="stacked" fill="outline" interface="popover" [(ngModel)]="u.tipus" [ngModelOptions]="{ standalone: true }">
        <ion-select-option *ngFor="let t of tipusok" [value]="t.kod">{{ t.nev }} ({{ t.jogalap }})</ion-select-option>
      </ion-select>
      <ion-input type="date" label="Kezdete" label-placement="stacked" fill="outline" [(ngModel)]="u.kezdete" [ngModelOptions]="{ standalone: true }"></ion-input>
      <ion-input type="date" label="Vége (ha lejár)" label-placement="stacked" fill="outline" [(ngModel)]="u.vege" [ngModelOptions]="{ standalone: true }"></ion-input>
      <ion-input label="Határozat száma, kiállítója" label-placement="stacked" fill="outline" maxlength="255" [(ngModel)]="u.hatarozat" [ngModelOptions]="{ standalone: true }"></ion-input>
      <ion-input label="Megjegyzés" label-placement="stacked" fill="outline" maxlength="500" [(ngModel)]="u.megjegyzes" [ngModelOptions]="{ standalone: true }"></ion-input>
      <div class="th-gombok">
        <ion-button size="small" (click)="ment()" [disabled]="mentes || !u.tipus">{{ mentes ? 'Mentés…' : 'Mentés' }}</ion-button>
        <ion-button size="small" fill="clear" color="medium" (click)="urlap = null">Mégse</ion-button>
      </div>
    </div>
  `,
  styles: [`
    .th-cim { font-size: 1rem; margin: 14px 0 4px; }
    .th-halvany { color: var(--ion-color-medium); font-size: 0.85rem; }
    .th-hiba { color: var(--ion-color-danger); font-size: 0.85rem; }
    .th-tabla { width: 100%; border-collapse: collapse; font-size: 0.88rem; margin-bottom: 6px; }
    .th-tabla th, .th-tabla td { text-align: left; padding: 3px 6px; border-bottom: 1px solid rgba(var(--ion-color-medium-rgb), 0.2); vertical-align: top; }
    .th-urlap { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; margin-top: 6px; }
    .th-gombok { display: flex; gap: 6px; align-items: center; }
  `],
})
export class TanuloHelyzetComponent implements OnChanges {
  @Input() oktazon = '';
  @Input() intezmeny = '';

  tipusok: { kod: string; nev: string; jogalap: string }[] = [];
  lista: Helyzet[] = [];
  urlap: Helyzet | null = null;
  fut = false;
  mentes = false;
  hiba = '';

  constructor(private http: HttpClient, private data: DataService) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['oktazon'] || ch['intezmeny']) this.toltes();
  }

  private get ut(): string {
    return `${encodeURIComponent(String(this.oktazon))}/${encodeURI(String(this.intezmeny || '').replace(/^\/+|\/+$/g, ''))}`;
  }

  async toltes(): Promise<void> {
    this.lista = []; this.urlap = null; this.hiba = '';
    if (!this.oktazon || !this.intezmeny) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/lemorzsolodas/helyzet/${this.ut}`));
      this.tipusok = r?.tipusok || [];
      this.lista = r?.helyzetek || [];
    } catch (e: any) {
      this.hiba = e?.status === 403 ? 'A hátrányos helyzet adatait csak adminisztrátor láthatja.' : (e?.error?.error || 'Az adatok most nem tölthetők be.');
    } finally { this.fut = false; }
  }

  tipusNev(kod: string): string { return this.tipusok.find((t) => t.kod === kod)?.nev || kod; }
  tipusJogalap(kod: string): string { return this.tipusok.find((t) => t.kod === kod)?.jogalap || ''; }

  szerkeszt(h: Helyzet | null): void {
    this.urlap = h ? { ...h } : { tipus: '', kezdete: '', vege: '', hatarozat: '', megjegyzes: '' };
  }

  async ment(): Promise<void> {
    if (!this.urlap?.tipus) return;
    this.mentes = true;
    try {
      await firstValueFrom(this.http.post(`/api/lemorzsolodas/helyzet/${this.ut}`, this.urlap));
      this.data.sendToast('Mentve.', 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally { this.mentes = false; }
  }

  async torol(h: Helyzet): Promise<void> {
    if (!h.id || !confirm(`Törlöd a bejegyzést? (${this.tipusNev(h.tipus)})`)) return;
    try {
      await firstValueFrom(this.http.delete(`/api/lemorzsolodas/helyzet/${h.id}/${encodeURI(String(this.intezmeny || '').replace(/^\/+|\/+$/g, ''))}`));
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    }
  }
}
